// The `en` source catalogue (FE-006). Every user-facing string in the app lives
// here as a dotted key. `en` is the source of truth: the key type is derived
// from this object (i18n/index.ts), so a missing or misspelled key is a compile
// error, and every non-en locale falls back here entry-by-entry (FE-006.3).
//
// Values are plain strings, or — for count nouns — a plural entry keyed by CLDR
// category (`one`/`other`, plus `few`/`many`/… when a locale needs them). `{name}`
// placeholders are filled at call time; `{n}` is supplied automatically by tp().
//
// es/pt translations are out of scope for this unit — this file is the English
// baseline they will mirror.

export const en = {
  // ── Shared, cross-surface copy (consolidated so a wording change lands once) ──
  'common.cancel': 'Cancel',
  'common.tryAgain': 'Try again',
  'common.backToHome': 'Back to home',
  'common.backToSignIn': 'Back to sign in',
  'common.sending': 'Sending…',
  'common.signOut': 'Sign out',
  'common.brand': 'Fexo Enterprise',
  'common.passkeys': 'Passkeys',
  'common.members': 'Members',
  'common.roles': 'Roles',
  'common.subdivisions': 'Subdivisions',
  'common.parties': 'Parties',
  'common.locations': 'Locations',
  'common.catalogs': 'Catalogs',
  'common.companies': 'Companies',
  'common.status': 'Status',
  'common.security': 'Security',
  'common.hqRoster': 'HQ roster',
  'common.activity': 'Activity',
  'common.config': 'Configuration',
  'common.errors': 'Error log',
  'common.fleet': 'Fleet',
  'common.devices': 'Devices',
  'common.devicesAndSessions': 'Devices & sessions',
  'common.me': 'Me',
  // Error fallbacks shared by every error-copy helper.
  'common.serverUnreachable': 'Could not reach the server. Check your connection.',
  'common.serverError': 'Something went wrong on our end. Please try again.',
  'common.tooManyAttempts': 'Too many attempts — give it a minute.',

  // ── FormScaffold (FE-006.1 — the seam every form inherits) ──
  'form.field.required': '{field} is required.',
  'form.action.save': 'Save',
  'form.select.placeholder': 'Select…',

  // ── DataTable (FE-006.1 — the seam every list inherits) ──
  'table.filter': 'Filter',
  'table.search': 'Search',
  'table.noMatches': 'No matches.',
  'table.selectRow': 'Select a row to see details.',
  'table.tableLabel': '{title} table',
  'table.backTo': '‹ {title}',
  'table.deskHint': '↑ ↓ move · Enter open · / search',
  'table.deskHintActions': '↑ ↓ move · Enter open · / search · right-click for actions',
  // Default count noun; a table supplies its own via `unitKey` (FE-006.4).
  'table.rows': { one: '{n} row', other: '{n} rows' },
  // Keyboard-shortcut descriptions (carried for a future on-screen cheat-sheet).
  'table.key.nextRow': 'Next row',
  'table.key.prevRow': 'Previous row',
  'table.key.openDetail': 'Open detail',
  'table.key.search': 'Search',
  // Inline expandable-rows variant (FE-023): the per-row disclosure toggle.
  'table.expandRow': 'Show details',
  'table.collapseRow': 'Hide details',

  // ── ExampleTable (the worked DataTable example) ──
  'example.col.name': 'Name',
  'example.col.id': 'ID',
  'example.col.category': 'Category',
  'example.col.owner': 'Owner',
  'example.col.status': 'Status',
  'example.col.updated': 'Updated',
  'example.detail.category': 'Category',
  'example.detail.owner': 'Owner',
  'example.detail.status': 'Status',
  'example.detail.updated': 'Updated',
  'example.menu.open': 'Open detail',
  'example.menu.copyId': 'Copy record ID',
  'example.menu.flag': 'Flag for review',
  'example.title': 'Records',
  'example.records': { one: '{n} record', other: '{n} records' },
  'example.searchPlaceholder': 'Search name, owner, status',
  'example.detailEmpty': 'Select a record to see its detail.',
  'example.emptyMessage': 'No records match that search.',

  // ── Primary navigation (navModel) ──
  'nav.home': 'Home',
  'nav.workOrders': 'Work Orders',
  'nav.workOrders.tab': 'Orders',
  'nav.trips': 'Trips',
  'nav.reports': 'Reports',
  'nav.admin': 'Admin',
  'nav.tabbar': 'Primary',
  'nav.company.switch': 'Switch company, current: {name}',

  // ── Coming-soon module copy (registered in app.ts) ──

  // Invitee landing bridge (/invite) — the new-user acceptance flow.
  'invite.accept.accepting': 'Setting up your account…',
  'invite.accept.oneMoment': 'One moment while we accept your invite.',
  'invite.accept.invalid.title': 'This invite can’t be used',
  'invite.accept.invalid.message':
    'This invite link is no longer valid — it may have expired, already been used, or been revoked. Ask whoever invited you to send a new one.',
  'invite.accept.error.title': 'Something went wrong',
  'invite.accept.error.message': 'We couldn’t complete your invite. Please try the link again in a moment.',

  // ── Confirm dialog (defaults; title/message are caller-supplied) ──
  'dialog.confirm': 'Confirm',
  // Fallback copy for the optional typed-reason field; callers can override.
  'dialog.reasonRequired': 'A reason is required.',
  'dialog.reasonTooLong': 'Max 512 characters.',

  // ── Health pill ──
  'health.checking': 'Checking…',
  'health.online': 'Online',
  'health.degraded': 'Degraded',
  'health.offline': 'Offline',
  'health.detail': 'Couchbase {status} · {ms} ms',
  'health.ariaStatus': 'System status: {label}. {detail}',

  // ── Build stamp ──
  'build.stamp': 'fexo · {env} · {sha}',
  'build.builtAt': 'Built at {time}',

  // ── Router not-found fallback (before setNotFound installs ErrorScreen) ──
  'common.notFound': 'Not Found',

  // ── Home ──
  'home.welcome': 'Welcome.',
  'home.captureSub': 'Record field work right from your phone.',
  'home.newRecord': 'New record',
  'home.newRecordBody': 'Capture a record in the field. Works offline and syncs the moment you reconnect.',
  'home.begin': 'Begin',
  'home.welcomeBack': 'Welcome back.',
  'home.recentActivity': 'Recent activity',
  'home.recentEmpty': 'Nothing yet. Records captured in the field will show up here.',

  // ── Error screen (presets; overrides are caller-supplied) ──
  'error.forbidden.title': "You don't have access to this",
  'error.forbidden.copy':
    'This part of the portal is limited to other roles. If you think you should have access, ask your administrator.',
  'error.notFound.title': "We can't find that page",
  'error.notFound.copy': "The page you're looking for doesn't exist, or it may have moved.",
  'error.offline.title': "You're offline",
  'error.offline.copy': "We can't reach the portal right now. Check your connection and try again.",
  'error.generic.title': 'Something went wrong',
  'error.generic.copy': 'We hit an unexpected problem. Please try again in a moment.',
  // A route's code failed to load (issue 279). Distinct from error.generic on
  // purpose: the usual cause is a new version having replaced the files this tab
  // was still pointing at, and "reload" is the actual fix — not a vague "try
  // again in a moment", which invites waiting for something that will never heal
  // on its own.
  // The service-worker update affordance (issue 293). Deliberately plain: the
  // page is not broken and nothing is lost by ignoring it, so the copy states a
  // fact rather than raising an alarm.
  'update.available': 'A new version of Fexo is ready.',
  'update.availableVersion': 'A new version of Fexo ({version}) is ready.',
  'update.reload': 'Reload',
  'update.later': 'Later',

  'error.chunk.title': "This page didn't finish loading",
  'error.chunk.copy':
    'The app may have been updated while this tab was open. Reload to get the latest version.',

  // ── Editor grid (empty section; other copy is caller-supplied) ──
  'editor.empty': 'Nothing to configure here yet.',
  // Distinct from editor.empty on purpose (issue 233): "nothing to configure" is a
  // fact about the catalog, "nothing matches" is a fact about what the admin just
  // typed — showing the wrong one sends them hunting for the wrong problem.
  'editor.filterEmpty': 'No rows here match that filter.',

  // ── Company picker ──
  'company.title': 'Choose a company',
  'company.message': 'Select the company you want to work in.',
  'company.current': '{role} · current',
  // Role labels (issue 728) — utils/roleLabel.ts. Eager: the company chooser
  // names the role before any admin slice could load.
  'role.admin': 'Administrator',
  'role.ops_manager': 'Operations manager',
  'role.dispatcher': 'Dispatcher',
  'role.driver': 'Driver',
  'role.viewer': 'Viewer',
  'role.hq_admin': 'HQ administrator',
  'company.multiPrompt':
    'You belong to more than one company. Pick the one to work in — you can switch later from your account.',

  // ── OAuth sign-in error copy (services/auth.ts) ──
  'authError.denied': 'Sign-in was cancelled. Try again, or use a magic link instead.',
  'authError.emailMissing':
    'Your provider didn’t share an email address, which we need to sign you in. Try a magic link instead.',
  'authError.emailUnverified':
    'Your Google email isn’t verified yet. Verify it with Google, or use a magic link.',
  'authError.deviceLimit':
    'You already have 3 active sessions. Try signing in again — the device you used least recently will be signed out to make room.',
  'authError.stateInvalid': 'That sign-in attempt expired or was invalid. Please try again.',
  'authError.generic': 'Something went wrong signing in. Try again, or use a magic link.',

  // ── Shared login/recovery form bits ──
  'common.email': 'Email',
  'common.emailPlaceholder': 'name@example.com',
  'common.emailInvalid': 'Enter a valid email address.',
  'common.lostPasskey': 'Lost your passkey?',

  // ── Login ──
  'login.title': 'Sign in',
  'login.sub': 'Enter your email — we’ll send you a magic link.',
  'login.send': 'Send magic link',
  'login.checkInbox': 'Check your inbox',
  'login.sentStatus': 'If this email is registered, a link is on its way to your inbox.',
  'login.footnote': 'We’ll never share your email.',
  'login.or': 'or',
  'login.continueApple': 'Continue with Apple',
  'login.continueGoogle': 'Continue with Google',
  'login.captchaFailed': 'We couldn’t verify that request. Refresh the page and try again.',
  'login.securitySignout': 'You were signed out for security. Please sign in again.',
  'login.roleChangedSignout': 'Your role changed — please sign in again to continue.',
  'login.sessionExpiredSignout': 'Your session expired. Please sign in again to continue.',

  // ── Me (account hub) ──
  'me.account': 'Account',
  'me.company': 'Company',
  'me.companyAria': 'Company: {name}. Switch company.',
  'me.switchCompany': 'Switch company',
  'me.switchCompanyMsg': 'Choose the company you want to work in.',
  'me.appearance': 'Appearance',
  'me.theme.aria': 'Theme',
  'me.theme.auto': 'Auto',
  'me.theme.light': 'Light',
  'me.theme.dark': 'Dark',
  // HQ standing self-visibility badge (RBAC-012b) — shown where the caller
  // already reads their identity (the desk account menu + the phone /me hub) so
  // an HQ holder sees their own all-company standing without opening the admin
  // member detail. Reuses FE-021's member-detail wording verbatim for one mental
  // model; holders without standing see no trace of it.
  'account.hqBadge': 'HQ — all companies',
  // Account menu (desk chrome, FE-010) — the dropdown off the top-nav trigger.
  'me.menu.aria': 'Account menu',
  // Language picker (the deferred FE-006 control). Options render as endonyms —
  // each language in its own name — which by convention are NOT translated, so
  // es/pt inherit these en values unchanged rather than overriding them.
  'me.language': 'Language',
  'me.locale.en': 'English',
  'me.locale.es': 'Español',
  'me.locale.pt': 'Português',
  'me.signedIn.phone': 'Signed in on this phone.',
  'me.signedIn.tablet': 'Signed in on this tablet.',
  'me.signedIn.desktop': 'Signed in on this computer.',
  'me.signedIn.generic': 'Signed in.',
  'me.signOutConfirm.title': 'Sign out?',
  'me.signOutConfirm.message': 'You will need a new magic link to sign back in on this device.',

  // ── Recovery ──
  'recovery.step1.title': 'Request recovery',
  'recovery.step1.detail': 'Enter your email and we’ll alert an administrator.',
  'recovery.step2.title': 'Verify your identity',
  'recovery.step2.detail': 'An admin contacts you on a registered channel to confirm it’s really you.',
  'recovery.step3.title': 'Sign in & set up again',
  'recovery.step3.detail': 'Your passkey is reset, so you can enroll a new one next time you sign in.',
  'recovery.sub':
    'We’ll help you get back in. For your security, an administrator verifies your identity before your passkey is reset.',
  'recovery.submit': 'Request recovery',
  'recovery.tooManyRequests': 'Too many requests — give it a minute, then try again.',
  'recovery.successTitle': 'Request received',
  'recovery.successLead':
    'If an account matches that email, an administrator has been notified to verify your identity and help you recover access.',
  'recovery.successNote':
    'You can close this page — the team will reach out on a channel you’ve registered with us.',

  // ── Passkey modals ──
  'passkeyModal.ok': 'OK',
  'passkeyModal.desktopRequired.title': 'Desktop required',
  'passkeyModal.desktopRequired.message':
    'Sensitive operations need a desktop signed in with a passkey. Switch to your desktop to continue.',
  'passkeyModal.stepUp.title': 'Verify it’s you',
  'passkeyModal.stepUp.message':
    'This action needs a passkey check. Use your fingerprint, face, or security key to continue.',
  'passkeyModal.stepUp.action': 'Use passkey',
  'passkeyModal.stepUp.progress': 'Waiting for your passkey…',
  'passkeyModal.enroll.title': 'Set up a passkey',
  'passkeyModal.enroll.message':
    'Your role can approve certifications and run reports. Add a passkey so those actions stay protected — it takes a few seconds.',
  'passkeyModal.enroll.action': 'Set up passkey',
  'passkeyModal.enroll.progress': 'Setting up your passkey…',
  'passkeyModal.enroll.cancel': 'Not now',
  'passkeyModal.error.cancelled': 'Passkey prompt was cancelled or timed out. Try again.',
  'passkeyModal.error.exists': 'This device already has a passkey for your account.',
  'passkeyModal.error.authenticator': 'Your authenticator couldn’t complete the request. Try again.',
  'passkeyModal.error.verifyFailed': 'Verification failed. Try again.',
  'passkeyModal.error.generic': 'Something went wrong. Try again.',

  // ── Sessions / devices ──
  'sessions.sub': "These are the devices currently signed in to your account. Revoke any you don't recognise.",
  'sessions.loading': 'Loading your sessions…',
  'sessions.loadError': 'Could not load your sessions. Check your connection.',
  'sessions.empty': 'No active sessions found.',
  'sessions.thisDevice': 'This device',
  'sessions.lastUsed': 'Last used',
  'sessions.ipAddress': 'IP address',
  'sessions.col.device': 'Device',
  'sessions.col.signedIn': 'Signed in',
  'sessions.actions': 'Actions',
  'sessions.reported': 'Reported — will be signed out and wiped when it next connects',
  'sessions.revoke': 'Revoke',
  'sessions.signOutThisDevice': 'Sign out this device',
  'sessions.revokeAria': 'Revoke {device}',
  'sessions.reportButton': "This wasn't me",
  'sessions.reportAria': 'Report {device} as not mine',
  'sessions.signOutOthers': 'Sign out all other devices',
  'sessions.signedOut': 'Signed out {device}.',
  'sessions.reportedStatus':
    "Reported {device}. It'll be signed out and its offline data wiped the next time it connects.",
  'sessions.unknownDevice': 'Unknown device',
  'sessions.unknown': 'Unknown',
  'sessions.staySignedIn': 'Stay signed in',
  'sessions.reportDevice': 'Report device',
  'sessions.signOutThisConfirm.title': 'Sign out this device?',
  'sessions.signOutThisConfirm.message':
    "You'll be signed out here and returned to the sign-in screen. Your other devices stay signed in.",
  'sessions.reportConfirm.title': 'Report this device?',
  'sessions.reportConfirm.message':
    "We'll sign this device out and erase anything it saved offline the next time it connects, and it'll have to set up a passkey again. Only do this if you don't recognise it.",
  'sessions.signOutOthersConfirm.title': 'Sign out all other devices?',
  'sessions.signOutOthersConfirm.confirm': 'Sign out others',
  'sessions.signOutOthersConfirm.body': {
    one: 'Your one other device will be signed out. This device stays signed in.',
    other: 'All {n} of your other devices will be signed out. This device stays signed in.',
  },
  'sessions.error.alreadyOut': 'That session was already signed out.',
  'sessions.error.currentDevice':
    'That’s the device you’re using now — use “Sign out this device” instead.',
  'sessions.error.generic': 'Something went wrong. Please try again.',
  'sessions.time.activeNow': 'Active now',
  'sessions.time.yesterday': 'Yesterday',
  'sessions.time.minutesAgo': { one: '{n} minute ago', other: '{n} minutes ago' },
  'sessions.time.hoursAgo': { one: '{n} hour ago', other: '{n} hours ago' },
  'sessions.time.daysAgo': { one: '{n} day ago', other: '{n} days ago' },
  'sessions.signedOutOthers': {
    one: 'Signed out {n} other device.',
    other: 'Signed out {n} other devices.',
  },
  'sessions.signedOutSomeFailed': {
    one: 'Signed out {n} device; {failed} could not be signed out.',
    other: 'Signed out {n} devices; {failed} could not be signed out.',
  },

  // ── Passkeys ──
  'passkeys.sub':
    'Passkeys protect sensitive actions like approvals and reports. Add a backup so you’re never locked out, and remove any you no longer use.',
  'passkeys.loading': 'Loading your passkeys…',
  'passkeys.unsupported':
    'This browser doesn’t support passkeys. Use a recent Chrome, Safari, or Firefox.',
  'passkeys.loadError': 'Could not load your passkeys. Check your connection.',
  'passkeys.empty': 'You haven’t set up a passkey yet.',
  'passkeys.defaultLabel': 'Passkey',
  'passkeys.defaultLabelLower': 'passkey',
  'passkeys.added': 'Added',
  'passkeys.unknown': 'Unknown',
  'passkeys.lastUsed': 'Last used',
  'passkeys.never': 'Never',
  'passkeys.setup': 'Set up a passkey',
  'passkeys.addAnother': 'Add another passkey',
  'passkeys.testStepUp': 'Test step-up',
  'passkeys.verified': 'Passkey verified — step-up works.',
  'passkeys.alreadyVerified': 'Already verified — your step-up window is still active.',
  'passkeys.checkCancelled': 'Passkey check cancelled or timed out.',
  'passkeys.verifyFailed': 'Step-up verification failed. Try again.',
  'passkeys.remove': 'Remove',
  'passkeys.removeAria': 'Remove {label}',
  'passkeys.addedStatus': 'Passkey added.',
  'passkeys.removeConfirm.title': 'Remove this passkey?',
  'passkeys.removeConfirm.lastMessage':
    'This is your only passkey. After removing it you’ll be asked to set one up again next time you sign in on a desktop.',
  'passkeys.removeConfirm.message':
    'This passkey will no longer work for verifying sensitive actions. Your other passkeys keep working.',
  'passkeys.keep': 'Keep',
  'passkeys.removedStatus': 'Passkey removed.',
  'passkeys.alreadyRemoved': 'That passkey was already removed.',
  'passkeys.removeFailed': 'Could not remove the passkey. Try again.',
  'passkeys.infoSub':
    'Passkeys are set up and managed on a desktop computer — sensitive actions that need them are desktop-only. Open the portal on your desktop to add or remove a passkey.',
  'passkeys.error.add':
    'Couldn’t add the passkey — it may have been cancelled or timed out, or that authenticator already has a passkey for your account.',
  'passkeys.error.exists': 'That authenticator already has a passkey on your account.',
  'passkeys.error.setupFailed': 'Your authenticator couldn’t complete setup. Try again.',
  'passkeys.error.forbidden': 'Passkey setup isn’t available for this account right now.',
  'passkeys.error.desktopOnly':
    'Passkeys are set up on a desktop computer. Open the portal on your desktop to add one.',
  'passkeys.error.notRequired':
    'Your role doesn’t use passkeys, so there is nothing to set up. If you think it should, ask an administrator.',
  'passkeys.error.addGeneric': 'Could not set up the passkey. Try again.',

  // ── Auth verify (magic-link landing) ──
  'authVerify.signingIn': 'Signing you in…',
  'authVerify.oneMoment': 'One moment while we verify your link.',
  'authVerify.capN': 'the maximum of {max} devices',
  'authVerify.capUnknown': 'the maximum number of devices',
  'authVerify.deviceLimit.title': 'Device limit reached',
  'authVerify.deviceLimit.message':
    "You're signed in on {cap}. Request a new sign-in link and try again — the device you used least recently will be signed out to make room.",
  'authVerify.deviceLimit.cta': 'Request a new sign-in link',
  'authVerify.invalid.title': 'Sign-in link invalid',
  'authVerify.invalid.expiredMessage': 'This link has expired or already been used. Request a fresh one.',
  'authVerify.invalid.message': 'We could not verify the link. Try requesting a fresh one.',

};
